import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bus,
  GitBranch,
  Pencil,
  PowerOff,
  RefreshCcw,
  ShieldAlert,
  UserRound,
} from 'lucide-react';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ClassificationStatus from '../components/common/ClassificationStatus';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Tabs from '../components/common/Tabs';
import Checkbox from '../components/forms/Checkbox';
import Input from '../components/forms/Input';
import Select from '../components/forms/Select';
import Textarea from '../components/forms/Textarea';
import { ACTIONS, RESOURCES, ROLES } from '../config/accessControl';
import { VOTER_CLASSIFICATIONS } from '../config/voterClassification';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { voterService } from '../services';

const formatDateTime = (value) =>
  value
    ? new Intl.DateTimeFormat('es-PY', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '—';

function DataField({ label, value, full = false }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-medium uppercase tracking-wide text-content-muted">{label}</dt>
      <dd className="mt-1 text-sm text-content-primary">{value || '—'}</dd>
    </div>
  );
}

function Timeline({ items, empty, renderTitle, renderDetail, dateKey }) {
  if (items.length === 0) {
    return <p className="rounded-control bg-slate-50 p-5 text-center text-sm text-content-muted">{empty}</p>;
  }
  return (
    <div className="space-y-4">
      {items.map((item) => (
        <div key={item.id} className="relative border-l-2 border-primary-100 pl-5">
          <span className="absolute -left-[5px] top-1 size-2 rounded-full bg-primary-500" />
          <p className="text-sm font-semibold text-content-primary">{renderTitle(item)}</p>
          <p className="mt-1 text-sm text-content-secondary">{renderDetail(item)}</p>
          <p className="mt-1.5 text-xs text-content-muted">{formatDateTime(item[dateKey])}</p>
        </div>
      ))}
    </div>
  );
}

export const normalizeVoterDetail = (payload = {}) => ({
  ...payload,
  calls: Array.isArray(payload.calls) ? payload.calls : [],
  audit: Array.isArray(payload.audit) ? payload.audit : [],
  duplicates: Array.isArray(payload.duplicates) ? payload.duplicates : [],
  possibleDuplicate: Boolean(
    payload.possibleDuplicate ||
    (Array.isArray(payload.duplicates) && payload.duplicates.length > 0)
  ),
  electoralRegister: payload.electoralRegister || payload.electoral_data || null,
  estado_verificacion:
    payload.estado_verificacion || payload.electoral_verification_status,
  direccion: payload.direccion || payload.address || '',
  barrio: payload.barrio || payload.neighborhood || '',
  referencia: payload.referencia || payload.reference || '',
  observaciones: payload.observaciones || payload.observations || '',
  jerarquia: payload.jerarquia || {
    puntero: { id: payload.puntero_id, name: payload.punteroNombre },
    coordinador: {
      id: payload.coordinador_id,
      name: payload.coordinadorNombre,
    },
    administradorGeneral: {
      id: payload.administrador_general_id,
      name: payload.administradorGeneralNombre,
    },
  },
});

function VoterDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can, canEdit } = usePermissions();
  const [voter, setVoter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [leaderOptions, setLeaderOptions] = useState([]);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [selectedLeaderId, setSelectedLeaderId] = useState('');
  const {
    register,
    handleSubmit,
    reset,
    watch,
  } = useForm();
  const selectedClassification = watch(
    'classification',
    voter?.classification || VOTER_CLASSIFICATIONS.WHITE
  );

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await voterService.getDetailForActor(id, user);
      setVoter(normalizeVoterDetail(response.data));
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    let mounted = true;
    voterService.getFilterOptions(user)
      .then((response) => mounted && setLeaderOptions(
        response.data.leaders.map((item) => ({ value: item.id, label: item.name }))
      ))
      .catch(() => mounted && setLeaderOptions([]));
    return () => { mounted = false; };
  }, [user]);

  const openEdit = () => {
    reset(voter);
    setActionError('');
    setModal('edit');
  };

  const persistEdit = async (values) => {
    setSaving(true);
    setActionError('');
    try {
      await voterService.updateForActor(id, {
        ...values,
        necesita_movil: Boolean(values.necesita_movil),
      }, user);
      setModal(null);
      setFeedback({ title: 'Datos actualizados', message: 'Los cambios se guardaron correctamente.' });
      await loadDetail();
    } catch (error) {
      setActionError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = persistEdit;

  const submitReassignment = async () => {
    if (!selectedLeaderId) {
      setActionError('Selecciona un puntero.');
      return;
    }
    setSaving(true);
    setActionError('');
    try {
      await voterService.reassignLeader(id, selectedLeaderId, user);
      setModal(null);
      setFeedback({ title: 'Puntero reasignado', message: 'La estructura y la auditoría fueron actualizadas.' });
      await loadDetail();
    } catch (error) {
      setActionError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleMobileNeed = async () => {
    try {
      await voterService.setMobileNeed(id, !voter.necesita_movil, user);
      setFeedback({
        title: 'Necesidad de móvil actualizada',
        message: voter.necesita_movil ? 'Se quitó la necesidad de móvil.' : 'El votante ahora necesita móvil.',
      });
      await loadDetail();
    } catch (error) {
      setFeedback({ title: 'No se pudo actualizar', message: error.message, variant: 'danger' });
    }
  };

  const deactivate = async () => {
    try {
      await voterService.deactivate(id, user);
      setConfirmDeactivate(false);
      setFeedback({ title: 'Votante desactivado', message: 'El estado del registro fue actualizado.' });
      await loadDetail();
    } catch (error) {
      setConfirmDeactivate(false);
      setFeedback({ title: 'No se pudo desactivar', message: error.message, variant: 'danger' });
    }
  };

  const tabs = useMemo(() => voter ? [
    {
      id: 'personal',
      label: 'Información personal',
      content: (
        <Card title="Datos personales y manuales" description={`Origen: ${voter.origen}`}>
          <dl className="grid gap-5 sm:grid-cols-2">
            <DataField label="Nombre completo" value={voter.nombreCompleto} />
            <DataField label="Cédula" value={voter.cedula} />
            <DataField label="Teléfono" value={voter.telefono} />
            <DataField label="Dirección" value={voter.direccion || voter.address} />
            <DataField label="Barrio" value={voter.barrio} />
            <DataField label="Referencia" value={voter.referencia} />
            <DataField label="Observaciones" value={voter.observaciones} full />
          </dl>
        </Card>
      ),
    },
    {
      id: 'electoral',
      label: 'Información electoral',
      content: (
        <Card title="Datos del padrón" description={voter.electoralRegister ? 'Coincidencia oficial encontrada' : 'Registro manual sin coincidencia'}>
          <dl className="grid gap-5 sm:grid-cols-2">
            <DataField label="Estado de verificación" value={voter.estado_verificacion} />
            <DataField label="Ciudad" value={voter.ciudad} />
            <DataField label="Local de votación" value={voter.local_votacion} />
            <DataField label="Mesa" value={voter.mesa} />
            <DataField label="Nombre oficial" value={voter.electoralRegister ? `${voter.electoralRegister.firstName} ${voter.electoralRegister.lastName}` : null} />
            <DataField label="Distrito oficial" value={voter.electoralRegister?.district} />
          </dl>
        </Card>
      ),
    },
    {
      id: 'structure',
      label: 'Estructura',
      content: (
        <Card title="Cadena de responsabilidad">
          <div className="space-y-3">
            {[
              ['Puntero responsable', voter.punteroNombre, voter.puntero_id],
              ['Coordinador del puntero', voter.coordinadorNombre, voter.jerarquia?.coordinador?.id],
              ['Administrador general', voter.administradorGeneralNombre, voter.jerarquia?.administradorGeneral?.id],
            ].map(([label, name, relationId]) => (
              <div key={label} className="flex items-center gap-3 rounded-control border p-4">
                <span className="grid size-9 place-items-center rounded-full bg-primary-50 text-primary-700"><GitBranch className="size-4" /></span>
                <div><p className="text-xs text-content-muted">{label}</p><p className="text-sm font-semibold text-content-primary">{name}</p><p className="text-[11px] text-slate-400">{relationId}</p></div>
              </div>
            ))}
          </div>
        </Card>
      ),
    },
    {
      id: 'classification',
      label: 'Clasificación',
      content: (
        <div className="grid gap-5 lg:grid-cols-2">
          <Card title="Clasificación actual">
            <ClassificationStatus
              classification={voter.classification}
              generatesExpense={voter.classification === VOTER_CLASSIFICATIONS.BLACK}
            />
            <p className="mt-4 rounded-control bg-slate-50 p-4 text-sm text-content-muted">
              La clasificación es operativa. Los montos se administran exclusivamente desde el módulo Financiero.
            </p>
          </Card>
          <Card title="Control de duplicados">
            {voter.possibleDuplicate ? (
              <Alert variant="warning" title="Posible duplicado">Existe una coincidencia pendiente de resolución.</Alert>
            ) : (
              <Alert variant="success" title="Sin alertas pendientes">No se detectaron duplicados sin resolver.</Alert>
            )}
            {voter.duplicates.map((duplicate) => (
              <div key={duplicate.id} className="mt-3 rounded-control border p-3 text-sm">
                <p className="font-medium text-content-primary">{duplicate.reason}</p>
                <p className="mt-1 text-xs text-content-muted">Estado: {duplicate.status}</p>
              </div>
            ))}
          </Card>
        </div>
      ),
    },
    {
      id: 'calls',
      label: 'Llamadas',
      count: voter.calls.length,
      content: <Card title="Llamadas de control"><Timeline items={voter.calls} empty="No hay llamadas registradas." renderTitle={(item) => item.result} renderDetail={(item) => item.notes || 'Sin observaciones'} dateKey="calledAt" /></Card>,
    },
    {
      id: 'history',
      label: 'Historial',
      count: voter.audit.length,
      content: <Card title="Historial y auditoría"><Timeline items={voter.audit} empty="No hay modificaciones registradas." renderTitle={(item) => item.action} renderDetail={(item) => item.detail} dateKey="createdAt" /></Card>,
    },
  ].filter((item) => (
    item.id !== 'calls'
    || user?.role === ROLES.ADMINISTRADOR_GENERAL
  )) : [], [voter, user?.role]);

  if (loading) {
    return <Card><div className="grid min-h-72 place-items-center"><LoadingSpinner label="Cargando detalle del votante" /></div></Card>;
  }

  if (loadError || !voter) {
    return (
      <Alert variant="danger" title="No se pudo abrir el votante">
        {loadError || 'El registro no está disponible.'}
      </Alert>
    );
  }

  if (user.role === ROLES.PUNTERO) {
    return (
      <div>
        <button onClick={() => navigate('/votantes')} className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-content-muted hover:text-content-primary">
          <ArrowLeft className="size-4" /> Volver a votantes
        </button>
        <Card title="Datos del votante" description="Información habilitada para el puntero responsable.">
          <dl className="grid gap-5 sm:grid-cols-2">
            <DataField label="Cédula" value={voter.cedula} />
            <DataField label="Nombre completo" value={voter.nombreCompleto} />
            <DataField label="Teléfono" value={voter.telefono} />
            <DataField label="Local de votación" value={voter.local_votacion} />
            <DataField label="Mesa" value={voter.mesa} />
          </dl>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate('/votantes')} className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-content-muted hover:text-content-primary">
        <ArrowLeft className="size-4" /> Volver a votantes
      </button>

      <div className="flex flex-col justify-between gap-5 xl:flex-row xl:items-start">
        <div className="flex items-start gap-4">
          <span className="grid size-12 shrink-0 place-items-center rounded-full bg-primary-100 text-primary-800"><UserRound className="size-6" /></span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold tracking-tight text-content-primary">{voter.nombreCompleto}</h2>
              <Badge variant={voter.estado === 'confirmado' ? 'success' : voter.estado === 'inactivo' ? 'neutral' : 'warning'} dot>{voter.estado}</Badge>
              {voter.possibleDuplicate && <Badge variant="danger">Posible duplicado</Badge>}
            </div>
            <p className="mt-1 text-sm text-content-muted">CI {voter.cedula} · Registrado el {formatDateTime(voter.fecha_registro)}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-content-secondary">
              <span className="rounded-full bg-slate-100 px-2.5 py-1">Puntero: {voter.punteroNombre}</span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1">Coordinador: {voter.coordinadorNombre}</span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1">Origen: {voter.origen}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {can(RESOURCES.VOTERS, ACTIONS.EDIT) && <Button variant="outline" size="sm" onClick={openEdit}><Pencil className="size-4" /> Editar</Button>}
          {can(RESOURCES.VOTERS, ACTIONS.ASSIGN) && <Button variant="outline" size="sm" onClick={() => { setSelectedLeaderId(voter.puntero_id); setActionError(''); setModal('reassign'); }}><RefreshCcw className="size-4" /> Reasignar puntero</Button>}
          {can(RESOURCES.VOTERS, ACTIONS.EDIT) && <Button variant="outline" size="sm" onClick={toggleMobileNeed}><Bus className="size-4" /> {voter.necesita_movil ? 'Quitar necesidad' : 'Necesita móvil'}</Button>}
          {can(RESOURCES.DUPLICATES, ACTIONS.VIEW) && <Button variant="outline" size="sm" onClick={() => setActiveTab('classification')}><ShieldAlert className="size-4" /> Consultar duplicado</Button>}
          {can(RESOURCES.VOTERS, ACTIONS.EDIT) && voter.estado !== 'inactivo' && <Button variant="danger" size="sm" onClick={() => setConfirmDeactivate(true)}><PowerOff className="size-4" /> Desactivar</Button>}
        </div>
      </div>

      {feedback && <Alert variant={feedback.variant || 'success'} title={feedback.title} className="mt-5" onClose={() => setFeedback(null)}>{feedback.message}</Alert>}

      <div className="mt-7 overflow-x-auto">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      <Modal open={modal === 'edit'} onClose={() => !saving && setModal(null)} title="Editar votante" description="Solo están habilitados los campos permitidos para tu rol." footer={<><Button variant="ghost" onClick={() => setModal(null)}>Cancelar</Button><Button type="submit" form="detail-edit-form" loading={saving}>Guardar cambios</Button></>}>
        {actionError && <Alert variant="danger" title="No se pudo guardar" className="mb-4">{actionError}</Alert>}
        <form id="detail-edit-form" onSubmit={handleSubmit(submitEdit)} className="grid gap-4 sm:grid-cols-2" noValidate>
          <Input id="detail-phone" label="Teléfono" disabled={!canEdit(RESOURCES.VOTERS, 'telefono')} {...register('telefono')} />
          <Input id="detail-address" label="Dirección" disabled={!canEdit(RESOURCES.VOTERS, 'direccion')} {...register('direccion')} />
          <Input id="detail-neighborhood" label="Barrio" disabled={!canEdit(RESOURCES.VOTERS, 'barrio')} {...register('barrio')} />
          <Input id="detail-reference" label="Referencia" disabled={!canEdit(RESOURCES.VOTERS, 'referencia')} {...register('referencia')} />
          <div className="sm:col-span-2">
            <Select id="detail-classification" label="Clasificación" disabled={!canEdit(RESOURCES.VOTERS, 'classification')} options={[{ value: VOTER_CLASSIFICATIONS.BLACK, label: 'Verde · Genera gasto' }, { value: VOTER_CLASSIFICATIONS.WHITE, label: 'Blanco · No genera gasto' }]} {...register('classification')} />
            <div className="mt-3">
              <ClassificationStatus
                classification={selectedClassification}
                generatesExpense={selectedClassification === VOTER_CLASSIFICATIONS.BLACK}
              />
            </div>
          </div>
          <Select id="detail-status" label="Estado" disabled={!canEdit(RESOURCES.VOTERS, 'estado')} options={[{ value: 'confirmado', label: 'Confirmado' }, { value: 'pendiente', label: 'Pendiente' }, { value: 'no_contactado', label: 'No contactado' }, { value: 'inactivo', label: 'Inactivo' }]} {...register('estado')} />
          <div className="sm:col-span-2"><Checkbox id="detail-mobile" label="Necesita móvil" disabled={!canEdit(RESOURCES.VOTERS, 'necesita_movil')} {...register('necesita_movil')} /></div>
          <Textarea id="detail-notes" label="Observaciones" className="sm:col-span-2" disabled={!canEdit(RESOURCES.VOTERS, 'observaciones')} {...register('observaciones')} />
        </form>
      </Modal>

      <Modal open={modal === 'reassign'} onClose={() => !saving && setModal(null)} title="Reasignar puntero" description="El coordinador y administrador general se actualizarán desde el nuevo puntero." footer={<><Button variant="ghost" onClick={() => setModal(null)}>Cancelar</Button><Button onClick={submitReassignment} loading={saving}>Reasignar</Button></>}>
        {actionError && <Alert variant="danger" title="No se pudo reasignar" className="mb-4">{actionError}</Alert>}
        <Select id="reassign-leader" label="Nuevo puntero" value={selectedLeaderId} onChange={(event) => setSelectedLeaderId(event.target.value)} options={leaderOptions} placeholder="Seleccionar puntero" />
      </Modal>

      <ConfirmDialog open={confirmDeactivate} onClose={() => setConfirmDeactivate(false)} onConfirm={deactivate} title="Desactivar votante" description="El registro permanecerá en el historial, pero quedará marcado como inactivo." confirmLabel="Sí, desactivar" danger />
    </div>
  );
}

export default VoterDetailPage;
